"use client";

import { type ClassicHeaderRuntimePayload, mergeClassicHeaderDesign } from "@multi-tenant-cms/types/menu";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { parseDualColorValue } from "../ui/lib/color-mode-value";
import styles from "./classic-header.module.css";
import { MegaPanelColumns, resolveMegaMenuBuilderPanelWidth } from "./MegaMenuPanel";

/**
 * Sets `--ch-<name>` (light side) and, when `value` is a per-field Light|Dark
 * override (`isDualColorValue` / `packages/builder/ui/lib/color-mode-value.ts`),
 * also `--ch-<name>-dark`. `classic-header.module.css` reads a `-resolved`
 * indirection (`--ch-bg-resolved: var(--ch-bg, ...)`) that's re-pointed at
 * `--ch-<name>-dark` under `[data-builder-canvas-mode="dark"]` (builder
 * canvas preview) or `.dark` (public site) — see that file's header comment.
 * Clears the `-dark` var when `value` isn't dual, so switching a field back
 * to a single value doesn't leave a stale dark override behind.
 *
 * This is a CSS-only mechanism deliberately: `ClassicHeader` is shared
 * runtime rendered by both the builder canvas (mode known only via a React
 * context / DOM attribute) and the public region (mode known only via a
 * `.dark` class on the document, no React context at all) — there is no
 * single JS-level "current site mode" this component could read reliably in
 * both places, but both places already resolve the right CSS custom property
 * automatically via the selectors above.
 */
export function applyDesignColorVar(root: HTMLElement, name: string, value: string): void {
  const dual = parseDualColorValue(value);
  if (dual) {
    root.style.setProperty(`--ch-${name}`, dual.light);
    root.style.setProperty(`--ch-${name}-dark`, dual.dark);
    return;
  }
  root.style.setProperty(`--ch-${name}`, value);
  root.style.removeProperty(`--ch-${name}-dark`);
}

/** Avoid using image `src` or media API paths as the logo link target. */
function safeLogoHref(logoHref: string | undefined, logoUrl?: string): string {
  const href = (logoHref ?? "/").trim() || "/";
  const url = logoUrl?.trim();
  if (url && href === url) {
    return "/";
  }
  if (/\/api\/media\//i.test(href)) {
    return "/";
  }
  if (/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i.test(href)) {
    return "/";
  }
  return href;
}
type MegaTabRoot = Extract<ClassicHeaderRuntimePayload["roots"][number], {
  itemKind: "mega_tab";
}>;
type MenuRoot = ClassicHeaderRuntimePayload["roots"][number];
function menuRootKey(root: MenuRoot, index: number): string {
  if (root.id) {
    return root.id;
  }
  return `root-${root.itemKind}-${root.sortOrder}-${index}`;
}
function panelHasContent(root: MegaTabRoot): boolean {
  if (root.megaMenuContent?.root) {
    return true;
  }
  return root.groups.some(group => group.links.length > 0);
}

/** String literals satisfy static a11y analyzers that cannot evaluate JSX booleans. */
const ARIA_EXPANDED_TRUE = {
  "aria-expanded": "true"
} as const;
const ARIA_EXPANDED_FALSE = {
  "aria-expanded": "false"
} as const;
const ARIA_HIDDEN_TRUE = {
  "aria-hidden": "true"
} as const;
const ARIA_HIDDEN_FALSE = {
  "aria-hidden": "false"
} as const;
export function ClassicHeader({
  payload
}: {
  payload: ClassicHeaderRuntimePayload;
}) {
  const [openTab, setOpenTab] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const openBuilderDropdownRef = useRef<HTMLDivElement>(null);

  // Content saved before `accentColor`/`ctaTextColor` existed (or any partial/
  // corrupted payload) must still render a complete, themeable header — fill
  // in missing tokens with the header-library defaults rather than leaving
  // the CSS custom properties below unset.
  const design = useMemo(() => mergeClassicHeaderDesign(payload.design), [payload.design]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    applyDesignColorVar(root, "bg", design.background);
    applyDesignColorVar(root, "text", design.textColor);
    applyDesignColorVar(root, "accent", design.accentColor);
    applyDesignColorVar(root, "cta-text", design.ctaTextColor);
    // MegaPanelColumns (reused panel content renderer, also shared by mega
    // menus) reads --mmh-text-color for its link color — theme it to match
    // the classic chrome's text color. Not dual-expanded: out of scope this
    // pass (see the module doc comment on generic/mega-menu elements), so a
    // dual text color's Light side is used for both site modes here.
    const textDual = parseDualColorValue(design.textColor);
    root.style.setProperty("--mmh-text-color", textDual ? textDual.light : design.textColor);
  }, [design]);
  const closeDropdown = useCallback(() => setOpenTab(null), []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    burgerRef.current?.focus();
  }, []);
  const closeAll = useCallback(() => {
    closeDropdown();
    closeDrawer();
  }, [closeDropdown, closeDrawer]);
  const toggleTab = useCallback((key: string) => {
    setOpenTab(current => current === key ? null : key);
  }, []);
  const toggleDrawer = useCallback(() => {
    setDrawerOpen(open => !open);
  }, []);
  const toggleAccordion = useCallback((tabId: string) => {
    setOpenAccordionId(current_0 => current_0 === tabId ? null : tabId);
  }, []);

  // Outside-click closes the open dropdown (matches shared/header.js's
  // document click-outside-root handler; the drawer has its own scrim/close).
  useEffect(() => {
    if (!openTab) {
      return;
    }
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openTab, closeDropdown]);
  useEffect(() => {
    const onKey = (e_0: KeyboardEvent) => {
      if (e_0.key === "Escape") {
        closeAll();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeAll]);

  // Builder-backed panels center under their trigger via `translate:
  // calc(-50% + var(--ch-builder-panel-shift-x)) 0` (see classic-header.module.css),
  // which can push a wide panel past the viewport edge for nav items near the
  // left/right of the bar. Legacy dropdowns stay anchored at `left: 0` and
  // don't need this — the ref is only attached to builder-backed panels, so
  // it's a no-op (`current` is null) whenever the open tab is legacy.
  useLayoutEffect(() => {
    if (!openTab) {
      return;
    }
    const el = openBuilderDropdownRef.current;
    if (!el) {
      return;
    }
    const inset = 12;
    const clampToViewport = () => {
      const rect = el.getBoundingClientRect();
      let shift = 0;
      if (rect.left < inset) {
        shift = inset - rect.left;
      } else if (rect.right > window.innerWidth - inset) {
        shift = window.innerWidth - inset - rect.right;
      }
      if (shift === 0) {
        el.style.removeProperty("--ch-builder-panel-shift-x");
      } else {
        el.style.setProperty("--ch-builder-panel-shift-x", `${shift}px`);
      }
    };
    clampToViewport();
    window.addEventListener("resize", clampToViewport);
    return () => window.removeEventListener("resize", clampToViewport);
  }, [openTab]);
  const anyOpen = openTab != null || drawerOpen;
  const logoHref = safeLogoHref(payload.logoHref, payload.logoUrl);
  const logoSrc = payload.logoUrl?.trim();
  return <div className={styles.root} ref={rootRef}>
			<div className={styles.bar}>
				<div className={styles.barInner}>
					<a className={styles.logo} href={logoHref}>
						{logoSrc ? <img alt={payload.logoAlt || ""} className={styles.logoImg} src={logoSrc} /> : <span>{payload.logoAlt || "Logo"}</span>}
					</a>

					<nav aria-label="Primary" className={styles.nav}>
						<ul className={styles.navList}>
							{payload.roots.map((root_0, rootIndex) => {
              const key_0 = menuRootKey(root_0, rootIndex);
              if (root_0.itemKind === "link") {
                return <li className={styles.navItem} key={key_0}>
											<a className={styles.navLink} href={root_0.href} rel={root_0.openInNewTab ? "noopener noreferrer" : undefined} target={root_0.openInNewTab ? "_blank" : undefined}>
												{root_0.label}
											</a>
										</li>;
              }
              if (root_0.itemKind !== "mega_tab") {
                return null;
              }
              const isOpen = openTab === root_0.id;
              const hasPanel = panelHasContent(root_0);
              const isBuilderPanel = Boolean(root_0.megaMenuContent?.root);
              const builderPanelWidth = isBuilderPanel ? resolveMegaMenuBuilderPanelWidth(root_0) : undefined;
              return <li className={styles.navItem} key={key_0}>
										<button className={`${styles.navBtn} ${isOpen ? styles.navBtnOpen : ""}`} {...hasPanel ? isOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE : {}} onClick={e_1 => {
                  e_1.stopPropagation();
                  toggleTab(root_0.id);
                }} type="button">
											{root_0.label}
											{hasPanel ? <svg aria-hidden="true" className={styles.navChevron} viewBox="0 0 10 6">
													<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
												</svg> : null}
										</button>
										{hasPanel && isOpen ? <div className={`${styles.dropdown} ${isBuilderPanel ? styles.dropdownBuilder : ""}`} ref={isBuilderPanel ? openBuilderDropdownRef : undefined} role="region" style={builderPanelWidth ? {
                  ["--ch-builder-panel-max" as string]: builderPanelWidth
                } as CSSProperties : undefined}>
												<MegaPanelColumns columnClassName={isBuilderPanel ? styles.dropdownBuilderCol : styles.dropdownCol} gridClassName={isBuilderPanel ? styles.dropdownBuilderGrid : styles.dropdownGrid} promo={undefined} root={root_0} />
											</div> : null}
									</li>;
            })}
						</ul>
					</nav>

					<div className={styles.actions}>
						{payload.cta.enabled ? <a className={styles.cta} href={payload.cta.href}>
								{payload.cta.label}
							</a> : null}
						<button aria-controls="ch-drawer" {...drawerOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-label={drawerOpen ? "Close menu" : "Open menu"} className={styles.burger} onClick={e_2 => {
            e_2.stopPropagation();
            toggleDrawer();
          }} ref={burgerRef} type="button">
							<span className={styles.burgerBar} />
							<span className={styles.burgerBar} />
							<span className={styles.burgerBar} />
						</button>
					</div>
				</div>
			</div>

			<button {...anyOpen ? ARIA_HIDDEN_FALSE : ARIA_HIDDEN_TRUE} aria-label="Close menu" className={styles.scrim} hidden={!anyOpen} onClick={closeAll} type="button" />

			<aside {...drawerOpen ? ARIA_HIDDEN_FALSE : ARIA_HIDDEN_TRUE} className={`${styles.drawer} ${drawerOpen ? styles.drawerOpen : ""}`} id="ch-drawer">
				<div className={styles.drawerHead}>
					<span>{payload.logoAlt || "Menu"}</span>
					<button aria-label="Close menu" className={styles.drawerClose} onClick={closeDrawer} type="button">
						×
					</button>
				</div>

				<nav aria-label="Mobile" className={styles.drawerNav}>
					{payload.roots.map((root_1, rootIndex_0) => {
          const key_1 = menuRootKey(root_1, rootIndex_0);
          if (root_1.itemKind === "link") {
            return <a className={styles.drawerLink} href={root_1.href} key={key_1} onClick={closeDrawer} rel={root_1.openInNewTab ? "noopener noreferrer" : undefined} target={root_1.openInNewTab ? "_blank" : undefined}>
									{root_1.label}
								</a>;
          }
          if (root_1.itemKind !== "mega_tab") {
            return null;
          }
          const expanded = openAccordionId === root_1.id;
          return <div className={styles.acc} key={key_1}>
								<button className={`${styles.accBtn} ${expanded ? styles.accBtnOpen : ""}`} {...expanded ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} onClick={() => toggleAccordion(root_1.id)} type="button">
									{root_1.label}
									<svg aria-hidden="true" className={styles.accCaret} viewBox="0 0 10 6">
										<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
									</svg>
								</button>
								<div className={`${styles.accPanel} ${expanded ? styles.accPanelOpen : ""}`}>
									<div className={styles.accInner}>
										<MegaPanelColumns columnClassName={styles.accCol} gridClassName={styles.accGrid} promo={undefined} root={root_1} />
									</div>
								</div>
							</div>;
        })}
				</nav>

				{payload.cta.enabled ? <a className={`${styles.cta} ${styles.ctaBlock}`} href={payload.cta.href} onClick={closeDrawer}>
						{payload.cta.label}
					</a> : null}
			</aside>
		</div>;
}